import { ApiResponse, PaginatedResponse, PaginationFilters } from './common';
import { SurveyQuestion } from './survey.types';

// ---------------------------------------------
// Shared Nested Types
// ---------------------------------------------
export interface SurveySetSiteSummary {
  id: number;
  name: string;
  languages: {
    id: number;
    name: string;
  }[];
  project: {
    id: number;
    name: string;
  };
}

export interface SurveySetSurveySummary {
  survey: {
    id: number;
    title: string;
    totalQuestions: number;
    status: boolean;
    createdAt: string;
    startDate: string | null;
    endDate: string | null;
    questionsJson: SurveyQuestion[];
  };
}

// ---------------------------------------------
// Survey Set List Item (used in Get All)
// ---------------------------------------------
export interface SurveySetListItem {
  id: number;
  title: string;
  posterCode: number;
  numberOfSurveys: number;
  site: SurveySetSiteSummary;
  status: boolean;
  createdAt: string;
  createdBy: string | null;
  startDate: string | null;
  endDate: string | null;
  submissionRule: SubmissionRule;
  currency: string;
  rewardAmount: number;
  claimType: ClaimType;
  stage: SurveySetStage;
  languages: { id: number; name: string; code: string }[];
  roleInfo: {
    name: string;
  };
}

export enum SubmissionRule {
  ONCE = 'ONCE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  NO_LIMIT = 'NO_LIMIT',
}

export enum ClaimType {
  COMPLETE_SURVEY_SET = 'COMPLETE_SURVEY_SET',
  PER_SURVEY = 'PER_SURVEY',
}

// ---------------------------------------------
// Survey Set Detailed View (Get By ID)
// ---------------------------------------------
export interface SurveySetDetails {
  id: number;
  title: string;
  numberOfSurveys: number;

  site: SurveySetSiteSummary;

  posterCode: number;
  currency: string;
  rewardAmount: number;
  claimType: ClaimType;

  maxParticipants?: number;
  participantLimit?: boolean;
  minParticipants?: number;

  status: boolean;
  startDate: string | null;
  endDate: string | null;
  timezone: string;

  greetingMsg: string;
  consentMsg: string;
  rewardMsg: string;

  submissionRule: SubmissionRule;

  surveys: SurveySetSurveySummary[];

  stage: SurveySetStage;

  languages: { id: number; name: string; code: string }[];
}

// ---------------------------------------------
// Create Survey Set DTO
// ---------------------------------------------
export interface CreateSurveySetDto {
  title: string;
  numberOfSurveys: number;
  siteId: number;

  participantLimit: boolean;
  maxParticipants?: number;
  minParticipants?: number;

  currency: string;
  claimType: ClaimType;
  rewardAmount: number;

  submissionRule: SubmissionRule;

  status: boolean;

  startDate: string;
  endDate: string;
  timezone: string;

  greetingMsg?: string;
  consentMsg?: string;
  rewardMsg?: string;

  survey: number[]; // survey IDs
  languages?: number[];
}

// ---------------------------------------------
// Create Response DTO
// ---------------------------------------------
export interface SurveySetDto {
  id: number;
  title: string;
  numberOfSurveys: number;

  currency: string;
  claimType: ClaimType;
  rewardAmount: number;
  posterCode: number;

  participantLimit: boolean;
  maxParticipants?: number;
  minParticipants?: number;

  siteId: number;
  submissionRule: SubmissionRule;

  stage: SurveySetStage;
  status: boolean;

  startDate: string;
  endDate: string;

  greetingMsg?: string;
  consentMsg?: string;
  rewardMsg?: string;

  isDeleted: boolean;

  createdAt: string;
  createdBy: string | null;

  updatedBy: string | null;
  updatedAt: string;

  deletedBy: string | null;
  deletedAt: string | null;
}

export type CreateSurveySetResponseDto = ApiResponse<SurveySetDto>;

// ---------------------------------------------
// Update DTO (Partial of Create)
// ---------------------------------------------
export interface UpdateSurveySetBody {
  title?: string;
  numberOfSurveys?: number;
  currency?: string;
  claimType: ClaimType; // mandatory
  rewardAmount?: number;
  posterCode?: number;
  participantLimit?: boolean;
  maxParticipants?: number;
  minParticipants?: number;
  siteId?: number;
  submissionRule?: SubmissionRule;
  stage?: SurveySetStage;
  status?: boolean;
  startDate?: string;
  endDate?: string;
  timezone?: string;
  greetingMsg?: string;
  consentMsg?: string;
  rewardMsg?: string;
  languages?: number[];
}

export type RowPermissions = {
  create?: boolean;
  edit?: boolean;
  delete?: boolean;
  preview?: boolean;
  statusToggle?: boolean;
  export?: boolean;
};

export type UpdateSurveySetResponseDto = ApiResponse<SurveySetDto>;

export enum SurveySetStage {
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
}

// ---------------------------------------------
// Get by ID Response DTO
// ---------------------------------------------
export type GetSurveySetByIdResponseDto = ApiResponse<SurveySetDetails>;

// ---------------------------------------------
// Get All Survey Sets
// ---------------------------------------------
export interface SurveySetFilters extends PaginationFilters {
  search?: string;
  status?: boolean;
  projectId?: number;
}

// ---------------------------------------------
// Get Survey Sets Stats
// ---------------------------------------------
export interface SurveySetsStats {
  total: number;
  active: number;
  inactive: number;
}
export interface SurveySetBySitesResponse {
  data: SurveySetBySites[];
}

export interface SurveySetBySites {
  id: number;
  title: string;
  posterCode: string;
  siteId: number;
}
export type GetSurveySetsStatsResponseDto = ApiResponse<SurveySetsStats>;

export type GetAllSurveySetsResponseDto = PaginatedResponse<SurveySetListItem[]>;
